import { DateTime } from "luxon";
import { s2Url } from "@seabrookstudios/types";
import { mapNestiFyzStatus } from "./mapNestifyzStatus";

/**
 * @param {import("@seabrookstudios/pitch2table-core").UpdateGameRequest} request
 * @returns {import("@seabrookstudios/pitch2table-core").UpdateGameRequest}
 */
const internalIdUpdateRequest = (request) => ({
  ...request,
  sizzle: undefined,
  bgg: undefined,
  personalGoal: undefined,
  customAuthors: undefined,
  gameType: undefined,
  referenceId: undefined,
  tags: undefined,
  feedbackEnabled: undefined,
});

/**
 * @param {string|undefined} timestamp
 * @returns {number|undefined}
 */
const getYearFromNestiFyzTimestamp = (timestamp) => {
  if (!timestamp) {
    return undefined;
  }

  return DateTime.fromISO(timestamp).year;
};

/**
 * @param {import("./parseExport").NestiFyzGame} nestifyzGame
 * @param {object} clients
 * @param {ReturnType<import("@seabrookstudios/pitch2table").useSaveGame>["mutateAsync"]} clients.saveGame
 * @param {ReturnType<import("@seabrookstudios/pitch2table").useUpdateGame>["mutateAsync"]} clients.updateGame
 * @returns {Promise<{ gameId: import("@seabrookstudios/types").GameId, needsPlaytestMetadata: boolean }>}
 */
export const importNestifyzGame = async (nestifyzGame, { saveGame, updateGame }) => {
  if (!nestifyzGame.id) {
    throw new Error("Missing NestiFyz ID");
  }

  const created = await saveGame({
    name: nestifyzGame.title || "Untitled game",
    tag: nestifyzGame.shortDescription || "",
    status: mapNestiFyzStatus(nestifyzGame.status),
  });

  if (!created?.game?.id) {
    throw new Error("Game was not created");
  }

  await updateGame({
    gameId: created.game.id,
    toUpdate: internalIdUpdateRequest({
      internalId: nestifyzGame.id,
      tags: (nestifyzGame.tags || []).join(","),
      sizzle: nestifyzGame.pitchVideoLink ? s2Url(nestifyzGame.pitchVideoLink) : undefined,
      bgg: undefined,
      personalGoal: undefined,
      customAuthors: undefined,
      gameType: undefined,
      referenceId: undefined,
      feedbackEnabled: false,
      description: nestifyzGame.longDescription,
      minPlayerCount: nestifyzGame.minPlayers,
      maxPlayerCount: nestifyzGame.maxPlayers,
      playerCount: `${nestifyzGame.minPlayers}-${nestifyzGame.maxPlayers}`,
      minDuration: nestifyzGame.minTime,
      maxDuration: nestifyzGame.maxTime,
      duration: `${nestifyzGame.minTime}-${nestifyzGame.maxTime} minutes`,
      durationType: "other",
      age: nestifyzGame.minAge,
      year: getYearFromNestiFyzTimestamp(nestifyzGame.createdAt),
    }),
  });

  return {
    gameId: created.game.id,
    needsPlaytestMetadata: (nestifyzGame.playtests || []).length > 0,
  };
};
